import React, { Component, Fragment, forwardRef, createRef, type Ref, type UIEvent } from 'react';
import { mergeRefs, blockEvent } from 'lib/react';

import cn from 'clsx';
import * as css from './HeaderDropdown.module.scss';

import { HeaderMenu, HeaderMenuItem, Button } from '@carbon/react';
import { ChevronDown } from '@carbon/react/icons';

export type HeaderDropdownMenuItem =
    | string
    | {
          icon?: false | ReactNode;
          onClick(): void;
          label: string;
          className?: string;
          disabled?: boolean;
          loading?: boolean;
      };
export interface HeaderDropdownProps {
    arrow?: boolean;
    direction?: 'left' | 'right';

    expanded?: boolean;
    onToggle?(): void;

    toggle: NonNullable<ReactNode>;
    items: Array<null | HeaderDropdownMenuItem> | ReactElement;

    title: string;
    label?: null | string;
    className?: string;
    iconClassName?: string;

    innerRef?: Ref<null | HTMLElement>;
}

class View extends Component<HeaderDropdownProps> {
    #ref = createRef<HTMLElement>();
    static defaultProps: Partial<HeaderDropdownProps> = { arrow: true };

    componentDidMount() {
        const el = this.#ref.current;
        if (this.props.expanded && el) el.setAttribute('aria-expanded', 'true');
    }
    componentDidUpdate(prevProps: HeaderDropdownProps) {
        const { expanded } = this.props;
        const el = this.#ref.current;
        if (el && typeof expanded === 'boolean' && expanded !== prevProps.expanded)
            el.setAttribute('aria-expanded', String(expanded));
    }

    // We have an aggressive click handler
    // to overtake the default functionality
    // of underlying components that closes the menu whenever anything inside is clicked.
    #handleClick = (e: UIEvent): void => {
        const { expanded, onToggle } = this.props;

        // Only catch clicks on the toggle anchor
        if (!this.#ref.current?.contains(e.target as Element)) return;

        // Stop the click if we're in a controlled state
        if (typeof expanded === 'boolean') blockEvent(e);

        // Call the toggle handler
        onToggle?.();
    };
    #renderMenuContent = (): ReactElement => {
        const { arrow, toggle } = this.props;

        return (
            <Fragment>
                {toggle}
                {arrow && <ChevronDown className={css.arrowIcon} />}
            </Fragment>
        );
    };

    render() {
        const { items, arrow, direction, toggle, title, label, className, iconClassName, innerRef, expanded, ...rest } =
            this.props;

        return (
            <HeaderMenu
                {...rest}
                ref={mergeRefs(this.#ref, innerRef)}
                onClick={this.#handleClick}
                className={cn(css.root, css[direction ?? 'left'], className)}
                aria-label={title}
                menuLinkName={label || ''}
                // This cannot be an inline arrow function because it's used as a component
                // and leads to ugly re-mounting, which in turn causes loss of DOM state
                renderMenuContent={this.#renderMenuContent}
                children={
                    Array.isArray(items)
                        ? items.map((p, i) => {
                              if (!p) return null;

                              if (typeof p === 'string') {
                                  return (
                                      <li
                                          key={`title-${i}`}
                                          className={css.labelItem}
                                          children={<span children={p} />}
                                      />
                                  );
                              }

                              let icon: ReactNode = '\xa0';
                              if (p.icon != null) icon = p.icon;

                              const isDisabled = p.disabled || p.loading;

                              return (
                                  <HeaderMenuItem
                                      key={i}
                                      as={Button}
                                      loading={p.loading}
                                      disabled={p.disabled}
                                      onClick={!isDisabled ? p.onClick : blockEvent}
                                      children={
                                          <div className={css.item}>
                                              {p.icon !== false && (
                                                  <div className={cn(css.itemIcon, iconClassName)} children={icon} />
                                              )}
                                              <span children={p.label} />
                                          </div>
                                      }
                                      className={cn(
                                          p.className,
                                          css.itemWrapper,
                                          isDisabled && css.disabled,
                                          i > 0 && items[i - 1] == null && css.spaced,
                                      )}
                                  />
                              );
                          })
                        : items
                }
            />
        );
    }
}

export const HeaderDropdown = forwardRef<HTMLAnchorElement, HeaderDropdownProps>((props, ref) => (
    <View {...props} innerRef={ref} />
));
